import { ModuleFragment, TrainingsContentModuleInput } from 'generated/resources/graphql'

import { AttachedFile } from 'modules/trainings/lib/content/AttachedFile'
import { Block } from 'modules/trainings/lib/content/Block'
import { ContentModule } from 'modules/trainings/lib/content/Module'
import { ModuleCollection } from 'modules/trainings/lib/content/ModuleCollection'

import { BlockMapper, BlockMapperRegistry } from './BlockMapperRegistry'
import { GQLContentBlock } from './types'

export class GraphQLContentMapper {
  private readonly blockRegistry: BlockMapperRegistry

  constructor(blockMappers: Array<BlockMapper<Block>>) {
    this.blockRegistry = new BlockMapperRegistry(blockMappers)
  }

  fromGraphQL(gqlModules: ModuleFragment[], trainingId: string): ModuleCollection {
    return new ModuleCollection({
      trainingId,
      modules: [...gqlModules]
        .sort((a, b) => a.position - b.position)
        .map((gqlModule) => this.moduleFromGraphQL(gqlModule)),
    })
  }

  toGraphQL(collection: ModuleCollection): TrainingsContentModuleInput[] {
    return collection.modules.map((module) => this.moduleToGraphQL(module))
  }

  private moduleFromGraphQL(gqlModule: ModuleFragment): ContentModule {
    return new ContentModule({
      uuid: gqlModule.uuid,
      title: gqlModule.title,
      description: gqlModule.description,
      thumbnail: gqlModule.thumbnail
        ? new AttachedFile({
            url: gqlModule.thumbnail.url || undefined,
            filename: gqlModule.thumbnail.filename || undefined,
          })
        : undefined,
      backgroundColor: gqlModule.style?.backgroundColor ?? undefined,
      fontSize: gqlModule.style?.fontSize ?? undefined,
      fontWeight: gqlModule.style?.fontWeight ?? undefined,
      blocks: [...gqlModule.contentBlocks]
        .sort((a, b) => a.position - b.position)
        .map((gqlBlock) => this.blockFromGraphQL(gqlBlock)),
    })
  }

  private moduleToGraphQL(module: ContentModule): TrainingsContentModuleInput {
    return {
      uuid: module.uuid,
      title: module.title,
      description: module.description,
      thumbnail: module.thumbnail
        ? {
            signedId: module.thumbnail.signedId,
            url: module.thumbnail.url,
          }
        : undefined,
      style: {
        backgroundColor: module.backgroundColor,
        fontSize: module.fontSize,
        fontWeight: module.fontWeight,
      },
      contentBlocks: module.blocks.map((block) => this.blockToGraphQL(block)),
    }
  }

  private blockFromGraphQL(gqlBlock: GQLContentBlock): Block {
    return this.blockRegistry.fromGraphQL(gqlBlock)
  }

  private blockToGraphQL(block: Block): TrainingsContentModuleInput['contentBlocks'][number] {
    return this.blockRegistry.toGraphQL(block)
  }
}
